// Invitations view - lists all pending game invitations

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

class InvitationsView extends BaseComponent {
  static properties = {
    _invitations: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
    .invitation-card {
      border: 1px solid #dee2e6;
      border-radius: 8px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .inviter {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
    }
    .inviter img {
      border-radius: 50%;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Invitation>} */
    this._invitations = [];
    this._loading = true;
    /** @type {string | null} */
    this._error = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadInvitations();
  }

  async _loadInvitations() {
    this._loading = true;
    this._error = null;
    try {
      const result = /** @type {Array<import('../../types/jsdoc-types.js').Invitation>} */ (
        await api.getMyInvitations()
      );
      this._invitations = result.sort(
        (
          /** @type {import('../../types/jsdoc-types.js').Invitation} */ a,
          /** @type {import('../../types/jsdoc-types.js').Invitation} */ b,
        ) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime(),
      );
    } catch (err) {
      console.error('Failed to load invitations:', err);
      this._error = 'Failed to load invitations. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  /** @param {string} dateString */
  _formatDate(dateString) {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  /** @param {string} emailHash */
  _getGravatarUrl(emailHash) {
    return `https://www.gravatar.com/avatar/${emailHash}?d=identicon&s=48`;
  }

  /** @param {string} gameId */
  _viewInvitation(gameId) {
    router.navigate(`/game/${gameId}/join`);
  }

  render() {
    return html`
      ${
        this._loading
          ? html`
            <div class="text-center text-muted py-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3">Loading invitations...</p>
            </div>
          `
          : this._error
            ? html` <div class="alert alert-danger" role="alert">${this._error}</div> `
            : this._invitations.length === 0
              ? html`
                <div class="text-center text-muted py-5">
                  <p>No pending invitations.</p>
                </div>
              `
              : html`
                <div class="d-flex flex-wrap gap-3">
                  ${this._invitations.map(
                    (inv) => html`
                      <div style="flex: 1 1 300px; min-width: 300px; max-width: 350px;">
                        <div class="invitation-card">
                          <h5>${inv.gameAdventureName}</h5>
                          <div class="inviter">
                            <img
                              src="${this._getGravatarUrl(inv.inviterEmailHash || '')}"
                              alt="${inv.inviterUsername}"
                              width="24"
                              height="24"
                            />
                            <span class="text-muted small">Invited by <strong>${inv.inviterUsername}</strong></span>
                          </div>
                          <div class="text-muted small mb-3">${this._formatDate(inv.createdAt || '')}</div>
                          <div class="mt-auto">
                            <button
                              class="btn btn-primary w-100"
                              @click="${() => this._viewInvitation(inv.gameId || '')}"
                            >
                              Join Game
                            </button>
                          </div>
                        </div>
                      </div>
                    `,
                  )}
                </div>
              `
      }
    `;
  }
}

customElements.define('invitations-view', InvitationsView);
